/**
 * Танк, out of seven boxes.
 *
 * What has to read from across a field is the *silhouette*, and a tank's is
 * four things in a fixed relationship: two tracks lower and longer than
 * everything else, a hull sitting between them, a turret set back on top of
 * that, and a gun sticking out well past the nose. Take away the overhanging
 * gun and it is an armoured car; take away the tracks and it is a shed.
 *
 * Built around the floor the driver stands on, exactly as the човен is, so
 * the machine can be put at the player's feet with no offset to remember.
 *
 * Unlit, like everything else in this scene - see scripts/check-lighting.mjs.
 */

import * as THREE from "three";
import type { Tank } from "../core/entity/tank.js";
import { TANK_HITS } from "../core/entity/tank.js";

/** Outside measurements, in blocks. */
const LENGTH = 3.4;
const WIDTH = 2.2;
const TRACK_W = 0.5;
const TRACK_H = 0.55;
const HULL_H = 0.7;
const TURRET_W = 1.3;
const TURRET_H = 0.6;
const GUN_LONG = 2.1;
const GUN_THICK = 0.18;

/** Olive, the tracks nearly black, and the gun a shade darker than the hull. */
const HULL = 0x4a5a3e;
const TRACK = 0x2a2a28;
const GUN = 0x3a4732;

interface TankGpu {
  readonly track: THREE.BufferGeometry;
  readonly hull: THREE.BufferGeometry;
  readonly turret: THREE.BufferGeometry;
  readonly gun: THREE.BufferGeometry;
  readonly hullMat: THREE.MeshBasicMaterial;
  readonly trackMat: THREE.MeshBasicMaterial;
  readonly gunMat: THREE.MeshBasicMaterial;
  readonly bases: readonly [THREE.Color, THREE.Color, THREE.Color];
}
let shared: TankGpu | null = null;

function tankGpu(): TankGpu {
  if (shared !== null) return shared;
  shared = {
    track: new THREE.BoxGeometry(TRACK_W, TRACK_H, LENGTH),
    hull: new THREE.BoxGeometry(WIDTH - TRACK_W, HULL_H, LENGTH * 0.92),
    turret: new THREE.BoxGeometry(TURRET_W, TURRET_H, TURRET_W * 1.2),
    gun: new THREE.BoxGeometry(GUN_THICK, GUN_THICK, GUN_LONG),
    hullMat: new THREE.MeshBasicMaterial({ color: HULL }),
    trackMat: new THREE.MeshBasicMaterial({ color: TRACK }),
    gunMat: new THREE.MeshBasicMaterial({ color: GUN }),
    bases: [new THREE.Color(HULL), new THREE.Color(TRACK), new THREE.Color(GUN)],
  };
  return shared;
}

/** Every tank darkens together with the day, because every tank is one tank. */
export function tintTanks(color: THREE.Color): void {
  const gpu = shared;
  if (gpu === null) return;
  gpu.hullMat.color.copy(gpu.bases[0]).multiply(color);
  gpu.trackMat.color.copy(gpu.bases[1]).multiply(color);
  gpu.gunMat.color.copy(gpu.bases[2]).multiply(color);
}

export class TankModel {
  readonly group = new THREE.Group();
  private readonly hull: THREE.Mesh;

  constructor() {
    const gpu = tankGpu();

    // The origin is the ground the tracks stand on, which is where the
    // driver's feet are. Everything below is built upward from nought.
    for (const sign of [1, -1]) {
      const track = new THREE.Mesh(gpu.track, gpu.trackMat);
      track.position.set(sign * (WIDTH / 2 - TRACK_W / 2), TRACK_H / 2, 0);
      this.group.add(track);
    }

    this.hull = new THREE.Mesh(gpu.hull, gpu.hullMat);
    this.hull.position.y = TRACK_H + HULL_H / 2 - 0.1;
    this.group.add(this.hull);

    const turret = new THREE.Mesh(gpu.turret, gpu.hullMat);
    // Set back of centre, which is where a turret sits and what stops the
    // thing reading as symmetrical - a box in the middle is a crate.
    turret.position.set(0, TRACK_H + HULL_H + TURRET_H / 2 - 0.1, -0.25);
    this.group.add(turret);

    const gun = new THREE.Mesh(gpu.gun, gpu.gunMat);
    gun.position.set(
      0, TRACK_H + HULL_H + TURRET_H / 2 - 0.1,
      GUN_LONG / 2 + TURRET_W * 0.4,
    );
    this.group.add(gun);
  }

  /**
   * Put it where it is, point it, and show what it has taken.
   *
   * The hull darkens with every shell it has taken - five is gone, and by
   * the fourth it is nearly black. There is no health bar in this game and a
   * tank is the only thing in it that has to advertise how close it is to
   * the end: whether to press an attack is the decision the whole machine
   * exists for.
   */
  setPose(tank: Tank): void {
    this.group.position.set(tank.x, tank.y, tank.z);
    this.group.rotation.y = tank.yaw;
    const left = Math.max(0, 1 - tank.hits / TANK_HITS);
    const paint = this.hull.material as THREE.MeshBasicMaterial;
    if (paint === tankGpu().hullMat && tank.hits > 0) {
      // Its own material once it is damaged, so one burnt tank does not
      // darken every other tank in the world.
      this.hull.material = new THREE.MeshBasicMaterial({ color: HULL });
    }
    if (this.hull.material !== tankGpu().hullMat) {
      (this.hull.material as THREE.MeshBasicMaterial).color
        .setHex(HULL)
        .multiplyScalar(0.35 + 0.65 * left);
    }
  }

  dispose(): void {
    if (this.hull.material !== tankGpu().hullMat) {
      (this.hull.material as THREE.MeshBasicMaterial).dispose();
    }
  }
}
